/**
 * Encryption Service for Multi-Account Management
 * Provides AES-256-GCM encryption for secure credential storage
 */

import { AuthSecret } from '../../../types/utils/authSecretTypes';

export interface EncryptedData {
  version: string;
  encryptedData: string;
  iv: string;
  salt: string;
}

export interface AccountCredentials {
  authSecret: AuthSecret;
  email: string;
  profilePicture?: string;
  lastUsed: number;
  rememberMe: boolean;
  persistenceDays: number;
  loginTimestamp: number;
  lastActivityTimestamp: number;
}

/**
 * Encryption service for secure credential storage
 */
export class EncryptionService {
  private static readonly ENCRYPTION_VERSION = '1.0';
  private static readonly ALGORITHM = 'AES-GCM';
  private static readonly KEY_LENGTH = 256;
  private static readonly IV_LENGTH = 12; // 96 bits for GCM
  private static readonly SALT_LENGTH = 16; // 128 bits
  private static readonly TAG_LENGTH = 16; // 128 bits for GCM tag
  private static readonly PBKDF2_ITERATIONS = 100000;

  /**
   * Generate a cryptographically secure random array
   */
  private static generateRandomBytes(length: number): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(length));
  }

  /**
   * Derive encryption key from session data using PBKDF2
   */
  private static async deriveKey(sessionId: string, salt: Uint8Array): Promise<CryptoKey> {
    // Use session ID as password material
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(sessionId),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: this.PBKDF2_ITERATIONS,
        hash: 'SHA-256'
      },
      keyMaterial,
      {
        name: this.ALGORITHM,
        length: this.KEY_LENGTH
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt account credentials
   */
  static async encrypt(
    credentials: AccountCredentials,
    sessionId: string
  ): Promise<EncryptedData> {
    try {
      // Generate random salt and IV
      const salt = this.generateRandomBytes(this.SALT_LENGTH);
      const iv = this.generateRandomBytes(this.IV_LENGTH);

      // Derive encryption key
      const key = await this.deriveKey(sessionId, salt);

      // Serialize credentials to JSON
      const plaintext = JSON.stringify(credentials);
      const encoder = new TextEncoder();
      const data = encoder.encode(plaintext);

      // Encrypt data
      const encrypted = await crypto.subtle.encrypt(
        {
          name: this.ALGORITHM,
          iv: iv,
          tagLength: this.TAG_LENGTH * 8 // Convert to bits
        },
        key,
        data
      );

      // Convert to base64 for storage
      const encryptedArray = new Uint8Array(encrypted);
      const encryptedBase64 = btoa(String.fromCharCode(...encryptedArray));
      const saltBase64 = btoa(String.fromCharCode(...salt));
      const ivBase64 = btoa(String.fromCharCode(...iv));

      return {
        version: this.ENCRYPTION_VERSION,
        encryptedData: encryptedBase64,
        iv: ivBase64,
        salt: saltBase64
      };
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt account credentials');
    }
  }

  /**
   * Decrypt account credentials
   */
  static async decrypt(
    encryptedData: EncryptedData,
    sessionId: string
  ): Promise<AccountCredentials> {
    try {
      // Validate version
      if (encryptedData.version !== this.ENCRYPTION_VERSION) {
        throw new Error(`Unsupported encryption version: ${encryptedData.version}`);
      }

      // Convert from base64
      const encrypted = new Uint8Array(
        atob(encryptedData.encryptedData)
          .split('')
          .map(char => char.charCodeAt(0))
      );
      const salt = new Uint8Array(
        atob(encryptedData.salt)
          .split('')
          .map(char => char.charCodeAt(0))
      );
      const iv = new Uint8Array(
        atob(encryptedData.iv)
          .split('')
          .map(char => char.charCodeAt(0))
      );

      // Derive decryption key
      const key = await this.deriveKey(sessionId, salt);

      // Decrypt data
      const decrypted = await crypto.subtle.decrypt(
        {
          name: this.ALGORITHM,
          iv: iv,
          tagLength: this.TAG_LENGTH * 8 // Convert to bits
        },
        key,
        encrypted
      );

      // Convert back to string and parse JSON
      const decoder = new TextDecoder();
      const plaintext = decoder.decode(decrypted);
      const credentials = JSON.parse(plaintext) as AccountCredentials;

      // Validate decrypted data structure
      if (!this.validateCredentials(credentials)) {
        throw new Error('Invalid decrypted credentials structure');
      }

      return credentials;
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt account credentials');
    }
  }

  /**
   * Validate credentials structure
   */
  private static validateCredentials(credentials: any): credentials is AccountCredentials {
    return (
      credentials &&
      typeof credentials === 'object' &&
      credentials.authSecret &&
      typeof credentials.authSecret.loginName === 'string' &&
      typeof credentials.authSecret.userName === 'string' &&
      credentials.authSecret.token &&
      typeof credentials.authSecret.token.accessToken === 'string' &&
      typeof credentials.email === 'string' &&
      typeof credentials.lastUsed === 'number' &&
      typeof credentials.rememberMe === 'boolean' &&
      typeof credentials.persistenceDays === 'number' &&
      typeof credentials.loginTimestamp === 'number' &&
      typeof credentials.lastActivityTimestamp === 'number'
    );
  }

  /**
   * Generate a unique session ID for encryption key derivation
   * This should be called once per browser session and stored in memory
   */
  static generateSessionId(): string {
    const randomBytes = this.generateRandomBytes(32);
    return btoa(String.fromCharCode(...randomBytes));
  }

  /**
   * Check if Web Crypto API is available
   */
  static isSupported(): boolean {
    return (
      typeof crypto !== 'undefined' &&
      typeof crypto.subtle !== 'undefined' &&
      typeof crypto.getRandomValues !== 'undefined'
    );
  }
}

/**
 * Session-based encryption key manager with cross-tab synchronization
 * Manages the session ID used for encryption key derivation
 */
export class SessionKeyManager {
  private static sessionId: string | null = null;
  private static readonly SESSION_KEY = 'smartmatrix_encryption_session';
  private static readonly SESSION_SYNC_KEY = 'smartmatrix_session_sync';
  private static storageListener: ((e: StorageEvent) => void) | null = null;

  /**
   * Initialize or get the current session ID with cross-tab sync
   */
  static getSessionId(): string {
    if (!this.sessionId) {
      // Try to restore from storage first
      this.sessionId = this.loadSessionFromStorage();
      
      if (!this.sessionId) {
        // Generate new session ID if none exists
        this.sessionId = EncryptionService.generateSessionId();
        this.saveSessionToStorage(this.sessionId);
        console.log('New encryption session initialized');
      } else {
        console.log('Encryption session restored from storage');
      }

      // Set up cross-tab synchronization
      this.setupCrossTabSync();
    }
    return this.sessionId;
  }

  /**
   * Clear the session ID from memory only (called on logout)
   * Note: Session ID remains in localStorage to allow decryption of saved accounts
   * Use clearSessionPermanently() to remove from storage completely
   */
  static clearSession(): void {
    this.sessionId = null;
    // Only clear from sessionStorage, keep in localStorage for saved accounts
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(this.SESSION_KEY);
    }
    this.cleanupCrossTabSync();
    console.log('Encryption session cleared from memory (localStorage preserved for saved accounts)');
  }

  /**
   * Permanently clear session ID from all storage
   * Should only be called when removing all accounts
   */
  static clearSessionPermanently(): void {
    this.sessionId = null;
    this.clearSessionFromStorage();
    this.cleanupCrossTabSync();
    console.log('Encryption session permanently cleared from all storage');
  }

  /**
   * Force regenerate the session ID and clear storage
   * This ensures a completely fresh start after logout
   */
  static forceResetSession(): void {
    console.log('Force resetting encryption session...');
    
    // Clear everything
    this.sessionId = null;
    this.clearSessionFromStorage();
    this.cleanupCrossTabSync();
    
    // Generate completely new session
    this.sessionId = EncryptionService.generateSessionId();
    this.saveSessionToStorage(this.sessionId);
    
    // Notify other tabs of the new session
    this.broadcastSessionChange();
    
    // Re-setup cross-tab sync
    this.setupCrossTabSync();
    
    console.log('Encryption session force reset completed');
  }

  /**
   * Check if session is initialized
   */
  static hasSession(): boolean {
    return this.sessionId !== null;
  }

  /**
   * Set up cross-tab synchronization
   */
  private static setupCrossTabSync(): void {
    if (typeof window === 'undefined' || this.storageListener) {
      return; // Already set up or not in browser
    }

    this.storageListener = (e: StorageEvent) => {
      if (e.key === this.SESSION_SYNC_KEY && e.newValue) {
        try {
          const syncData = JSON.parse(e.newValue);
          if (syncData.sessionId && syncData.sessionId !== this.sessionId) {
            console.log('SessionKeyManager: Syncing session from another tab');
            this.sessionId = syncData.sessionId;
            // Update local storage to match (sessionId is guaranteed to be string here)
            this.saveSessionToStorage(syncData.sessionId);
          }
        } catch (error) {
          console.warn('SessionKeyManager: Failed to parse sync data:', error);
        }
      }
    };

    window.addEventListener('storage', this.storageListener);
  }

  /**
   * Clean up cross-tab synchronization
   */
  private static cleanupCrossTabSync(): void {
    if (typeof window !== 'undefined' && this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
  }

  /**
   * Broadcast session change to other tabs
   */
  private static broadcastSessionChange(): void {
    if (typeof window === 'undefined' || !this.sessionId) {
      return;
    }

    try {
      const syncData = {
        sessionId: this.sessionId,
        timestamp: Date.now()
      };
      localStorage.setItem(this.SESSION_SYNC_KEY, JSON.stringify(syncData));
      
      // Remove the sync key after a short delay to trigger storage event
      setTimeout(() => {
        localStorage.removeItem(this.SESSION_SYNC_KEY);
      }, 100);
    } catch (error) {
      console.warn('SessionKeyManager: Failed to broadcast session change:', error);
    }
  }

  /**
   * Load session ID from storage
   */
  private static loadSessionFromStorage(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      // Try localStorage first (persistent), then sessionStorage
      return localStorage.getItem(this.SESSION_KEY) || 
             sessionStorage.getItem(this.SESSION_KEY);
    } catch (error) {
      console.warn('Failed to load encryption session from storage:', error);
      return null;
    }
  }

  /**
   * Save session ID to storage
   */
  private static saveSessionToStorage(sessionId: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      // Save to both localStorage (persistent) and sessionStorage (current session)
      localStorage.setItem(this.SESSION_KEY, sessionId);
      sessionStorage.setItem(this.SESSION_KEY, sessionId);
    } catch (error) {
      console.warn('Failed to save encryption session to storage:', error);
    }
  }

  /**
   * Clear session ID from storage
   */
  private static clearSessionFromStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem(this.SESSION_KEY);
      sessionStorage.removeItem(this.SESSION_KEY);
      localStorage.removeItem(this.SESSION_SYNC_KEY);
    } catch (error) {
      console.warn('Failed to clear encryption session from storage:', error);
    }
  }
}